"use client";

import {JSX, useEffect, useRef} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useKeenSlider} from "keen-slider/react";

import {SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import "./team-content.scss";
import "keen-slider/keen-slider.min.css";

export type TeamContentProps = {
    icon: string; // icon url
    title: string;
    paragraph?: string;
    paragraphs?: Array<string>;
    slides?: Array<{url: string; alt?: string}>;
    hasSlider?: boolean; // if false, section is text-only
    layout?: "image-grid"; // optional variant for images grid
    space?: SpaceProps;
    anchorId?: string;
};

function TeamContent(props: TeamContentProps): JSX.Element {
    const {icon, title, paragraph, paragraphs, slides, hasSlider, layout} = props;
    const dotsRef = useRef<HTMLDivElement | null>(null);
    const sectionVars = useSectionSpace(props.space);
    const setActiveDot = (activeIdx: number): void => {
        const container = dotsRef.current;
        if (!container) return;
        const buttons = container.querySelectorAll("button.team-content__dot");
        buttons.forEach((btn, idx) => {
            if (idx === activeIdx) {
                btn.classList.add("is-active");
            } else {
                btn.classList.remove("is-active");
            }
        });
    };
    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        loop: true,
        rubberband: true,
        slides: {perView: 1, spacing: 0},
        created(s) {
            setActiveDot(s.track.details.rel);
        },
        slideChanged(s) {
            setActiveDot(s.track.details.rel);
        },
    });
    const images = Array.isArray(slides) ? slides.slice(0, 3) : [];
    const showSlider = hasSlider !== false && images.length > 0 && layout !== "image-grid";
    const showGrid = layout === "image-grid" && Array.isArray(slides) && slides.length > 0;

    // Ensure active dot is set after initial render and whenever slides change
    useEffect(() => {
        if (instanceRef.current) {
            setActiveDot(instanceRef.current.track.details.rel);
        } else {
            // Fallback: mark the first as active if slider not ready yet
            setActiveDot(0);
        }
    }, [images.length]);

    return (
        <section className="team-content__section" style={sectionVars} id={props.anchorId}>
            <ContainerBase>
                <div className={`team-content__row${showSlider ? "" : " team-content__row--single"}`}>
                    <div className="team-content__content">
                        <div className="team-content__header">
                            <span className="team-content__icon">
                                <SmartImage src={icon} alt="" width={24} height={24} aria-hidden="true" />
                            </span>
                            <Typography as="h3" size={{desktop: 20, mobile: 18}} weight={600} className="team-content__title">
                                {title}
                            </Typography>
                        </div>

                        {Array.isArray(paragraphs) && paragraphs.length > 0 ? (
                            <div className="team-content__text">
                                {paragraphs.map((p, idx) => (
                                    <Typography
                                        as="div"
                                        key={`p-${idx}`}
                                        size={16}
                                        weight={400}
                                        color="paragraph"
                                        className="team-content__paragraph"
                                        innerHTML={p}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Typography as="p" size={16} weight={400} color="paragraph" className="team-content__paragraph" innerHTML={paragraph} />
                        )}
                    </div>

                    {showSlider && (
                        <div className="team-content__media">
                            <div className="team-content__slider-wrap">
                                <div ref={sliderRef} className="keen-slider team-content__slider">
                                    {images.map((img, index) => (
                                        <div className="keen-slider__slide team-content__slide" key={`${img.url}-${index}`}>
                                            <div className="team-content__slide-inner">
                                                <SmartImage
                                                    src={img.url}
                                                    alt={img.alt ?? ""}
                                                    fill
                                                    sizes="(max-width: 1024px) 100vw, 720px"
                                                    className="team-content__image"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    aria-label="Previous"
                                    className="team-content__arrow team-content__arrow--prev"
                                    onClick={() => instanceRef.current?.prev()}
                                >
                                    <SmartImage
                                        src={assetPath("/images/icons/navigation/arrow-right2.svg")}
                                        alt=""
                                        width={16}
                                        height={16}
                                        aria-hidden="true"
                                    />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Next"
                                    className="team-content__arrow team-content__arrow--next"
                                    onClick={() => instanceRef.current?.next()}
                                >
                                    <SmartImage
                                        src={assetPath("/images/icons/navigation/arrow-right2.svg")}
                                        alt=""
                                        width={16}
                                        height={16}
                                        aria-hidden="true"
                                        style={{transform: "rotate(180deg)"}}
                                    />
                                </button>

                                <div ref={dotsRef} className="team-content__dots">
                                    {images.map((_, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            aria-label={`Go to slide ${idx + 1}`}
                                            className="team-content__dot"
                                            onClick={() => instanceRef.current?.moveToIdx(idx)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    {showGrid && (
                        <div className="team-content__media">
                            <div className="team-content__image-grid">
                                {(slides ?? []).slice(0, 4).map((img, index) => (
                                    <div className="team-content__grid-item" key={`${img.url}-${index}`}>
                                        <div className="team-content__grid-item-inner">
                                            <SmartImage
                                                src={img.url}
                                                alt={img.alt ?? ""}
                                                fill
                                                sizes="(max-width: 1024px) 45vw, 180px"
                                                className="team-content__image"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </ContainerBase>
        </section>
    );
}

export default TeamContent;
